import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import Loading from "@components/shared/Loading";
import Label from "@components/form/Label";
import SearchInput from "@components/form/SearchInput";
import Table from "@components/layout/Table";
import NoteIndicatorBadge from "@components/notes/NoteIndicatorBadge";
import RowNotesModal from "@components/notes/RowNotesModal";
import { formatVolume } from "@utils/formatVolume";
import { useAllLevel0Resources } from "@hooks/query/useAllLevel0Resources";
import { useNoteCounts } from "@hooks/query/useNoteCounts";
import { useAppContext } from "@context/AppContext";
import { DBTables } from "@utils/constants/dbTables";

type Props = {
  level1ResourceId: string;
};

const Level0ResourceTable: React.FunctionComponent<Props> = ({
  level1ResourceId,
}) => {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = React.useState("");
  const [openNotesRow, setOpenNotesRow] = React.useState<{
    id: string;
    label: string;
  } | null>(null);
  const navigate = useNavigate();
  const { checkPermissions } = useAppContext();
  const { data, isLoading } = useAllLevel0Resources({
    params: {
      parentId: level1ResourceId,
    },
  });

  const level0Ids = React.useMemo(
    () => (data ?? []).map((item: any) => item.id),
    [data],
  );
  const { data: noteCounts = {} } = useNoteCounts(DBTables.Level0WRS, level0Ids);
  const canViewNotes = checkPermissions(["ViewNotes"]);

  if (isLoading) {
    return (
      <div className="py-20">
        <Loading />
      </div>
    );
  }

  const tableFields = [
    {
      title: t("level0wrs.name"),
      name: "name",
    },
    {
      title: t("level0wrs.identifier"),
      name: "identifier",
    },
    {
      title: t("level0wrs.volume"),
      name: "volume",
    },
    ...(canViewNotes
      ? [
          {
            title: t("subscriber.notes"),
            name: "notes",
          },
        ]
      : []),
    {
      title: t("common.actions"),
      name: "actions",
    },
  ];

  const tableData = data
    .filter((item: any) =>
      identifier.length
        ? item.identifier.toLowerCase().includes(identifier.toLowerCase()) ||
          item.name.toLowerCase().includes(identifier.toLowerCase())
        : true,
    )
    .map((item: any) => {
      return {
        name: item?.name ?? "",
        identifier: item?.identifier ?? "",
        description: item?.description ?? "",
        volume: item?.yield ? formatVolume(item.yield) : 0,
        notes: (
          <NoteIndicatorBadge
            count={noteCounts[item.id]}
            onClick={() =>
              setOpenNotesRow({
                id: item.id,
                label: item?.identifier ?? item?.name ?? "",
              })
            }
          />
        ),
        actions: (
          <Select
            placeholder={t("common.actions")}
            options={[
              ...(checkPermissions(["UpdateLevel0Resource"])
                ? [
                    {
                      label: t("common.edit"),
                      value: `/polestar/level1wrs/${level1ResourceId}/level0wrs/${item.id}/edit`,
                    },
                  ]
                : []),
            ]}
            onChange={e => {
              if (e?.value) {
                navigate(e.value);
              }
            }}
            menuPortalTarget={document.body}
            isSearchable={false}
          />
        ),
      };
    });

  return (
    <>
      <header className="flex items-end gap-4">
        <div className="max-w-sm">
          <Label htmlFor="name">{t("level0wrs.filter.identifier")}</Label>
          <SearchInput
            id="identifier"
            onChange={e => setIdentifier(e.target.value.toLowerCase())}
          />
        </div>
        {checkPermissions(["CreateLevel0Resource"]) && (
          <Link
            className="ml-auto btn-secondary text-sm rounded"
            to={`/polestar/level1wrs/${level1ResourceId}/level0wrs/create`}
          >
            {t("water_class.create_new_zone")}
          </Link>
        )}
      </header>

      <Table
        fields={tableFields}
        data={tableData}
        stickyHeader
        tableHeaderClassName="relative z-10"
        loading={isLoading}
      />
      <RowNotesModal
        open={openNotesRow !== null}
        referenceTable={DBTables.Level0WRS}
        referenceId={openNotesRow?.id ?? ""}
        artifactName={openNotesRow?.label}
        onClose={() => setOpenNotesRow(null)}
      />
    </>
  );
};

export default Level0ResourceTable;
